import React, { useEffect, useRef, useState } from "react";
import { getRarityToneClass } from "../utils/rarityPresentation";

const EDGE_ALIGNED_PREVIEW_MAX_SIZE_PX = 96;

function toCssLength(value) {
  if (value == null || value === "") return undefined;
  return typeof value === "number" ? `${value}px` : value;
}

export default function PreviewCountBadge({
  value,
  tone = "dark",
  rarityName = "",
  cornerRadius,
  edgeAligned = false,
  overlay = true,
  title,
  className = "",
  style,
}) {
  const badgeRef = useRef(null);
  const [autoEdgeAligned, setAutoEdgeAligned] = useState(false);
  const text = value == null || value === "" ? "" : String(value).trim();

  useEffect(() => {
    if (!overlay || !text) {
      setAutoEdgeAligned(false);
      return undefined;
    }

    const badgeNode = badgeRef.current;
    const previewNode = badgeNode?.parentElement;
    if (!previewNode) {
      setAutoEdgeAligned(false);
      return undefined;
    }

    const updateAlignment = () => {
      const rect = previewNode.getBoundingClientRect();
      const previewSize = Math.min(rect.width || 0, rect.height || 0);
      setAutoEdgeAligned(previewSize > 0 && previewSize <= EDGE_ALIGNED_PREVIEW_MAX_SIZE_PX);
    };

    updateAlignment();

    if (typeof ResizeObserver === "undefined") {
      if (typeof window === "undefined") return undefined;
      window.addEventListener("resize", updateAlignment);
      return () => window.removeEventListener("resize", updateAlignment);
    }

    const observer = new ResizeObserver(() => updateAlignment());
    observer.observe(previewNode);
    return () => observer.disconnect();
  }, [overlay, text]);

  if (!text) return null;

  const classes = [
    "preview-count-badge",
    overlay ? "preview-count-badge--overlay" : "",
    edgeAligned || autoEdgeAligned ? "preview-count-badge--edge-aligned" : "",
    tone ? `preview-count-badge--${tone}` : "",
    tone === "rarity" && rarityName ? getRarityToneClass(rarityName) : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={badgeRef}
      className={classes}
      style={{
        ...(cornerRadius != null
          ? { "--preview-count-badge-corner-radius": toCssLength(cornerRadius) }
          : null),
        ...style,
      }}
      title={title ?? text}
    >
      <span className="preview-count-badge__value" data-digits={text.length}>
        {text}
      </span>
    </div>
  );
}
